import * as Sentry from "@sentry/react";
import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Spacer from "../../../components/Spacer";
import SignatureRequestsTab from "./SignatureRequestsTab";

import { useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { rbacPermissionsQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

import { getIsUserSuperuser } from "../../../selectors/app";

type SignatureRequestsAdminProps = {
	tab: "requests";
	isSuperUserAdmin?: boolean;
};

export const signatureRequestsLoader = () => {
	const queries: Array<() => SimplifiedQuery> = [() => rbacPermissionsQuery("signature_request_templates")];

	return dataLoader([queries]);
};

const SignatureRequestsAdmin = (props: SignatureRequestsAdminProps) => {
	const { tab, isSuperUserAdmin = false } = props;
	const isUserSuperuser = useSelector(getIsUserSuperuser);
	const {
		value: docSigningEnabled,
		query: { isLoading: isLoadingDocSigningFF },
	} = useFeatureFlagQuery("ff-is-document-signing-enabled", false);
	const { data: rbacPermissions, isLoading: isRbacLoading } =
		useRbacPrimaryRolePermissions("signature_request_templates");

	const isLoadingAny = isLoadingDocSigningFF || isRbacLoading;

	const hasViewPermission = rbacPermissions?.includes("signature_request_templates:viewAdmin");

	const renderInfoBox = (heading: string, message: string) => (
		<div>
			<AdminTitle includeSpacer={false}>{heading}</AdminTitle>
			<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
				<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
				<div className="font-semibold">{message}</div>
			</div>
		</div>
	);

	const renderGatedContent = (): React.ReactNode => {
		if (isLoadingAny) return null;
		// Handled in server.py, this is a fallback redirect.
		if (isSuperUserAdmin && !isUserSuperuser) {
			return <Navigate to="/administration/signature-requests" replace />;
		}
		if (!docSigningEnabled) {
			return renderInfoBox("Signature Requests", "This feature is not enabled for your organization.");
		}
		if (!hasViewPermission) {
			return renderInfoBox("Signature Requests", "Missing required permissions.");
		}
		switch (tab) {
			case "requests":
				return (
					<>
						<Spacer height={4} />
						<SignatureRequestsTab />
					</>
				);
		}
	};

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<AdminContainer>{renderGatedContent()}</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default SignatureRequestsAdmin;
